import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import TableWithState from "../../components/common/TableWithState";
import Loader from "../../components/common/Loader";
import EmptyState from "../../components/common/EmptyState";
import DestructiveConfirm from "../../components/common/DestructiveConfirm";
import { getFrameworks, deleteFramework } from "../../api/frameworks";
import { useToast } from "../../components/common/ToastProvider";
import Button from "../../ui/Button";

export default function FrameworkList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();

  const [limit] = useState(20);
  const [offset, setOffset] = useState(0);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState();
  const [confirmEntry, setConfirmEntry] = useState(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["frameworks", { limit, offset, q, sort }],
    queryFn: () => getFrameworks({ limit, offset, q, sort }),
    keepPreviousData: true,
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteFramework(id),
    onSuccess: () => {
      success("Framework deleted");
      queryClient.invalidateQueries({ queryKey: ["frameworks"] });
    },
    onError: (err) => {
      showError(err?.detail || "Failed to delete framework");
    },
  });

  const columns = useMemo(
    () => [
      { label: "Name", accessor: "name", sortable: true },
      { label: "Code", accessor: "code", sortable: true },
      { label: "Description", accessor: "description", sortable: false },
    ],
    []
  );

  const handleSearch = (e) => {
    e.preventDefault();
    setOffset(0);
  };

  return (
    <div>
      <Breadcrumbs items={[{ label: "Frameworks" }]} />
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
        <h1>Frameworks</h1>
        <Button type="button" data-testid="new-framework" onClick={() => navigate("/frameworks/new")} variant="primary">
          New Framework
        </Button>
      </div>
      <form onSubmit={handleSearch} style={{ marginBottom: "12px", display: "flex", gap: "8px" }}>
        <input
          data-testid="framework-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name or code"
        />
        <Button type="submit" variant="secondary">
          Search
        </Button>
      </form>
      {isLoading && <Loader />}
      {isError && <div>Error: {error?.detail || "Failed to load frameworks"}</div>}
      {data && (
        <TableWithState
          columns={columns}
          data={data.items}
          total={data.total}
          limit={data.limit}
          offset={data.offset}
          sort={sort}
          onSort={setSort}
          onPageChange={setOffset}
          onRowClick={(row) => navigate(`/frameworks/${row.id}`)}
          renderActions={(row) => (
            <div style={{ display: "flex", gap: "8px" }}>
              <Button type="button" onClick={() => navigate(`/frameworks/${row.id}/edit`)} variant="secondary">
                Edit
              </Button>
              <Button type="button" onClick={() => setConfirmEntry({ id: row.id, name: row.name })} variant="destructive">
                Delete
              </Button>
            </div>
          )}
        />
      )}
      {!isLoading && data?.items?.length === 0 && <EmptyState title="No frameworks found" />}
      <DestructiveConfirm
        actionId="FRAMEWORK_DELETE"
        open={Boolean(confirmEntry)}
        resourceDisplayName={confirmEntry?.name}
        onCancel={() => setConfirmEntry(null)}
        onConfirm={() => {
          if (confirmEntry?.id) {
            deleteMutation.mutate(confirmEntry.id);
            setConfirmEntry(null);
          }
        }}
      />
    </div>
  );
}
